import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Subject, Observable, forkJoin, firstValueFrom } from 'rxjs';
import { takeUntil, filter, take } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { finalize, takeUntil as rxTakeUntil } from 'rxjs/operators';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { AuthService } from '../../../services/auth/auth.service';
import { Location } from '../../../models/location/Location.model';
import { Room } from '../../../models/location/Room.model';
import { environment } from '../../../environment/environment';
import { ConfirmResetDialogComponent } from '../duna-dashboard/confirm-reset-dialog.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import {
  DUNA_EVENT_NOTIFICATION_PREFERENCE_ROWS,
  NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY,
  NotificationPreferencesMatrix,
  normalizeNotificationPreferencesMatrix,
} from '../../../constants/duna-notification-preference-types';
import {
  cateringFieldEnabledOrgKey,
  EVENT_CATERING_FIELD_EVENT_TYPES,
  EVENT_CATERING_FIELD_TYPE_LABELS,
  type EventCateringFieldEventType,
} from '../../../services/component-data-source/data/event/event-catering-field.util';
import {
  modulesFieldEnabledOrgKey,
  EVENT_MODULES_FIELD_EVENT_TYPES,
  EVENT_MODULES_FIELD_TYPE_LABELS,
  type EventModulesFieldEventType,
} from '../../../services/component-data-source/data/event/event-modules-field.util';
import { GROUPS_HIDE_EMPTY_AT_LOCATION_KEY } from '../duna-user-groups-location/user-groups-location-display.util';

export interface OrgPriceGroupRow {
  id: number;
  label: string;
  is_active: boolean;
  sort_order?: number;
}

export interface OrgBookableResourceRow {
  id: number;
  label: string;
  is_active: boolean;
  sort_order?: number;
}

export interface GroupCalendarRoleOption {
  role: string;
  label: string;
  alwaysOn?: boolean;
}

export const GROUP_CALENDAR_VIEW_ROLES_KEY = 'group_calendar_view_roles';
export const GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY =
  'group_calendar_user_show_all_locations';
export const GROUP_CALENDAR_EDIT_PATH_IDS_KEY = 'group_calendar_edit_path_ids';
export const GROUP_CALENDAR_DEFAULT_VIEW_ROLES = ['uddansvarlig'];
export const GROUP_CALENDAR_ALWAYS_ON_ROLES = ['orgbruger', 'orgadmin'];

/** All configurable roles — superadmin is excluded from the UI but always has access in code. */
export const GROUP_CALENDAR_ROLE_OPTIONS: GroupCalendarRoleOption[] = [
  { role: 'primaer', label: 'Primær' },
  { role: 'sekundaer', label: 'Sekundær' },
  { role: 'uddansvarlig', label: 'Uddannelsesansvarlig' },
  { role: 'uddassistent', label: 'Uddannelsesassistent' },
  { role: 'orgbruger', label: 'Org-bruger', alwaysOn: true },
  { role: 'orgadmin', label: 'Org-admin', alwaysOn: true },
];

export interface OrgSettingCheckboxDef {
  key: string;
  default: boolean;
  label: string;
  description: string;
}

/** Organisation setting: moduler tæller kun én gang (samme nøgle som org_settings API). */
export function isOrgCountModuleOnceEnabled(
  settings: Record<string, unknown> | null | undefined
): boolean {
  const v = settings?.['count_module_once'];
  return v === true || v === 1 || v === '1' || v === 'true';
}

export const ORG_SETTINGS_CHECKBOXES: OrgSettingCheckboxDef[] = [
  {
    key: 'count_module_once',
    default: false,
    label: 'Moduler tælles kun én gang',
    description:
      'Moduler skal ikke tælle flere gange, ved deltagelse i samme modul.',
  },
  {
    key: 'events_show_all_locations_users',
    default: false,
    label: 'Vis brugere fra alle lokationer ved øvelser',
    description:
      'Se brugere fra ALLE lokationer, som er tilmeldt til øvelser (normalt kun egen lokations brugere).',
  },
  {
    key: 'events_allow_register_others',
    default: true,
    label: 'Tillad tilmelding af andre brugere til events',
    description: 'Tilmelding af andre brugere til events (standard aktiveret).',
  },
  {
    key: 'events_allow_self_exclude_participation',
    default: true,
    label: 'Tillad fravalg af egen deltagelse ved tilmelding',
    description:
      'Brugere kan vælge kun at tilføje andre deltagere uden selv at deltage (via deltagelsesformularen). Deaktiver for at kræve egen deltagelse.',
  },
  {
    key: 'events_allow_users_edit_own_events',
    default: true,
    label: 'Tillad brugere at redigere egne events',
    description:
      'Brugere kan redigere events, de selv har oprettet (standard aktiveret).',
  },
  {
    key: 'events_uddansvarlig_cross_location_edit',
    default: false,
    label: 'Tillad uddansvarlig at redigere på tværs af lokationer',
    description:
      'Uddansvarlig kan redigere events på alle lokationer i samme organisation (deaktiveret standard).',
  },
  {
    key: 'events_auto_move_from_waitlist',
    default: false,
    label: 'Flyt automatisk fra venteliste',
    description:
      'Flyt automatisk fra venteliste ved plads på primære liste (deaktiveret standard).',
  },
  {
    key: 'events_notify_on_change',
    default: false,
    label: 'E-mail ved ændring af event-felter',
    description:
      'Send mail ved ændring af specifikke felter på event – dato / tid / lokation (deaktiveret standard).',
  },
  {
    key: 'events_auto_complete_instructors_on_ended_events',
    default: false,
    label: 'Auto-sæt instruktører til deltaget på afsluttede events',
    description:
      'Når aktiveret, sættes instruktører automatisk til "Deltaget/completed" på events der er afsluttet (deaktiveret standard).',
  },
  {
    key: 'events_hide_course_capacity_public',
    default: false,
    label: 'Skjul antal pladser for kurser i kalender',
    description:
      'Skjul tilmeldte, samlet antal pladser og ledige pladser for kursus-begivenheder i kalender- og listevisning.',
  },
];

export const USER_ADMIN_SETTINGS_CHECKBOXES: OrgSettingCheckboxDef[] = [
  {
    key: 'users_notify_email_conflict_on_sync',
    default: false,
    label: 'E-mail til orgadmin ved nye bruger-sync konflikter',
    description:
      'Send én samlet e-mail per orgadmin i organisationen, når Keycloak-sync opretter nye e-mail-konflikter (deaktiveret som standard). Gælder kun orgadmins i samme organisation.',
  },
];

export const CUSTOMER_ARCHIVE_SETTINGS_CHECKBOXES: OrgSettingCheckboxDef[] = [
  {
    key: 'customer_archive_enabled',
    default: false,
    label: 'Aktiver kundearkiv',
    description:
      'Gør kundearkiv tilgængeligt for organisationen (deaktiveret som standard).',
  },
];

export const USER_GROUPS_SETTINGS_CHECKBOXES: OrgSettingCheckboxDef[] = [
  {
    key: 'groups_enabled',
    default: false,
    label: 'Aktiver brugergrupperinger',
    description:
      'Gør brugergrupperinger tilgængelige for organisationen (deaktiveret som standard).',
  },
  {
    key: 'group_calendar_enabled',
    default: false,
    label: 'Aktiver gruppe-kalender',
    description:
      'Gør gruppe-kalenderen tilgængelig for organisationen (deaktiveret som standard).',
  },
  {
    key: GROUPS_HIDE_EMPTY_AT_LOCATION_KEY,
    default: true,
    label: 'Skjul tomme grupper på lokationer',
    description:
      'Vis kun brugergrupper på en lokation, hvor mindst én bruger fra lokationen er i gruppen (aktiveret som standard). Gruppe-kalenderen vises kun når der er relevante grupper.',
  },
];

export const MATERIALS_CATALOG_MODE_KEY = 'materials_catalog_mode';
export const MATERIALS_PROVIDER_ORG_ID_KEY = 'materials_provider_organization_id';
export const MATERIALS_BORROWER_ORG_IDS_KEY = 'materials_borrower_organization_ids';
export const MATERIALS_BUFFER_DAYS_KEY = 'materials_availability_buffer_days';

export const MATERIALS_SETTINGS_CHECKBOXES: OrgSettingCheckboxDef[] = [
  {
    key: 'materials_enabled',
    default: true,
    label: 'Aktiver materiel',
    description:
      'Tillad materialanmodninger på events og materiel-administration (aktiveret som standard). UI vises først når der er oprettet materiel.',
  },
  {
    key: 'materials_require_approval',
    default: true,
    label: 'Kræv godkendelse af anmodninger',
    description:
      'Når deaktiveret godkendes anmodninger automatisk hvis der er ledigt materiel.',
  },
  {
    key: 'materials_track_stock',
    default: true,
    label: 'Hold styr på lager og ledighed',
    description:
      'Bloker godkendelse/booking hvis der ikke er nok materiel i perioden.',
  },
  {
    key: 'materials_show_availability_calendar',
    default: true,
    label: 'Vis ledighedskalender i materiel-oversigt',
    description: 'Kalender-fane til at se bookinger (erstatter ekstern kalender).',
  },
  {
    key: 'materials_multi_location_enabled',
    default: false,
    label: 'Flere lagerlokationer',
    description:
      'Fordel antal på flere lagre og vælg lokation ved booking (deaktiveret som standard).',
  },
  {
    key: 'materials_notify_on_request',
    default: true,
    label: 'E-mail ved ny anmodning',
    description: 'Kvittering til bestiller og besked til materiel-administratorer.',
  },
  {
    key: 'materials_notify_on_approve',
    default: true,
    label: 'E-mail ved godkendelse',
    description: 'Bekræftelse til bestiller (og evt. instruktører).',
  },
  {
    key: 'materials_notify_on_reject',
    default: true,
    label: 'E-mail ved afvisning',
    description: 'Besked til bestiller når anmodning afvises.',
  },
  {
    key: 'materials_notify_instructors_on_approve',
    default: true,
    label: 'E-mail til instruktører ved godkendelse',
    description: 'Send også godkendelsesmail til eventets instruktører.',
  },
];

export interface EventCategoryColorItem {
  category: string;
  color: string;
}

type ResourceOverviewEventType = 'education' | 'course' | 'exercise';

const RESOURCE_OVERVIEW_LOCATION_KEY =
  'resource_overview_default_location_guids';
const RESOURCE_OVERVIEW_ROOM_KEY = 'resource_overview_default_room_guids';
const RESOURCE_OVERVIEW_EVENT_TYPES_KEY =
  'resource_overview_default_event_types';
const RESOURCE_OVERVIEW_SHOW_MANUAL_LOCATIONS_KEY =
  'resource_overview_show_manual_locations';
const RESOURCE_OVERVIEW_SHOW_LOCATION_ROWS_KEY =
  'resource_overview_show_location_rows';
const RESOURCE_OVERVIEW_ORG_RESOURCE_KEY =
  'resource_overview_default_org_resource_ids';
const EVENTS_ADDITIONAL_ROOMS_ENABLED_KEY = 'events_additional_rooms_enabled';

const RESOURCE_OVERVIEW_EVENT_TYPES: {
  value: ResourceOverviewEventType;
  label: string;
}[] = [
  { value: 'education', label: 'Uddannelser' },
  { value: 'course', label: 'Kurser' },
  { value: 'exercise', label: 'Øvelser' },
];

/** Per event_type (API values course / education / exercise) — UI labels are Danish. */
export interface RegisterDeadlineTypeDef {
  eventType: 'course' | 'education' | 'exercise';
  label: string;
  description: string;
}

export interface CateringFieldTypeDef {
  eventType: EventCateringFieldEventType;
  label: string;
  description: string;
}

export interface ModulesFieldTypeDef {
  eventType: EventModulesFieldEventType;
  label: string;
  description: string;
}

export const EVENT_CATERING_FIELD_TYPES: CateringFieldTypeDef[] =
  EVENT_CATERING_FIELD_EVENT_TYPES.map((eventType) => ({
    eventType,
    label: EVENT_CATERING_FIELD_TYPE_LABELS[eventType],
    description:
      'Når aktiveret, kan man vælge forplejning ved oprettelse og redigering af denne begivenhedstype (standard aktiveret).',
  }));

export const EVENT_MODULES_FIELD_TYPES: ModulesFieldTypeDef[] =
  EVENT_MODULES_FIELD_EVENT_TYPES.map((eventType) => ({
    eventType,
    label: EVENT_MODULES_FIELD_TYPE_LABELS[eventType],
    description:
      'Når aktiveret, vises modul-side (side 2) og moduler i serieplanlæggeren for denne begivenhedstype (standard aktiveret).',
  }));

export const REGISTER_DEADLINE_EVENT_TYPES: RegisterDeadlineTypeDef[] = [
  {
    eventType: 'course',
    label: 'Kursus',
    description:
      'Når aktiveret, kan man kun tilmelde sig indtil det angivne antal dage før eventets start (via join).',
  },
  {
    eventType: 'education',
    label: 'Uddannelse',
    description:
      'Når aktiveret, kan man kun tilmelde sig indtil det angivne antal dage før eventets start (via join).',
  },
  {
    eventType: 'exercise',
    label: 'Øvelse',
    description:
      'Når aktiveret, kan man kun tilmelde sig indtil det angivne antal dage før eventets start (via join).',
  },
];

export const UNREGISTER_EVENT_TYPES: RegisterDeadlineTypeDef[] = [
  {
    eventType: 'course',
    label: 'Kursus',
    description:
      'Når aktiveret, kan deltagere afmelde sig indtil det angivne antal dage før eventets start.',
  },
  {
    eventType: 'education',
    label: 'Uddannelse',
    description:
      'Når aktiveret, kan deltagere afmelde sig indtil det angivne antal dage før eventets start.',
  },
  {
    eventType: 'exercise',
    label: 'Øvelse',
    description:
      'Når aktiveret, kan deltagere afmelde sig indtil det angivne antal dage før eventets start.',
  },
];

/** Standard primær påmindelse: 1 dag før start, aktiveret. */
export const DEFAULT_EVENT_REMINDER_ENABLED = true;
export const DEFAULT_EVENT_REMINDER_DAYS = 1;

/** Påmindelse før event (samme tre typer som tilmeldingsfrist). */
export const EVENT_REMINDER_NOTIFY_TYPES: RegisterDeadlineTypeDef[] = [
  {
    eventType: 'course',
    label: 'Kursus',
    description:
      'Primær påmindelse ved tilmelding (samme skabelon som “Husk event”). Du kan tilføje en valgfri ekstra påmindelse på et andet tidspunkt.',
  },
  {
    eventType: 'education',
    label: 'Uddannelse',
    description:
      'Primær påmindelse ved tilmelding (samme skabelon som “Husk event”). Du kan tilføje en valgfri ekstra påmindelse på et andet tidspunkt.',
  },
  {
    eventType: 'exercise',
    label: 'Øvelse',
    description:
      'Primær påmindelse ved tilmelding (samme skabelon som “Husk event”). Du kan tilføje en valgfri ekstra påmindelse på et andet tidspunkt.',
  },
];

@Component({
  selector: 'app-duna-org-settings',
  standalone: true,
  imports: [
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatExpansionModule,
    MatIconModule,
    MatDialogModule,
  ],
  templateUrl: './duna-org-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-org-settings.component.scss',
})
export class DunaOrgSettingsComponent implements OnInit, OnDestroy {
  @Input() organizationId: number | null = null;
  private static readonly RESOURCE_OVERVIEW_LIST_PAGE_SIZE = 10000;

  checkboxDefs = ORG_SETTINGS_CHECKBOXES;
  userAdminCheckboxDefs = USER_ADMIN_SETTINGS_CHECKBOXES;
  customerArchiveCheckboxDefs = CUSTOMER_ARCHIVE_SETTINGS_CHECKBOXES;
  userGroupsCheckboxDefs = USER_GROUPS_SETTINGS_CHECKBOXES;
  materialsCheckboxDefs = MATERIALS_SETTINGS_CHECKBOXES;
  materialsCatalogMode: 'own_org' | 'provider_org' = 'own_org';
  materialsProviderOrgId: number | null = null;
  materialsBorrowerOrgIdsText = '';
  materialsBufferDays = 0;
  registerDeadlineTypes = REGISTER_DEADLINE_EVENT_TYPES;
  eventCateringFieldTypes = EVENT_CATERING_FIELD_TYPES;
  eventModulesFieldTypes = EVENT_MODULES_FIELD_TYPES;
  unregisterEventTypes = UNREGISTER_EVENT_TYPES;
  eventReminderNotifyTypes = EVENT_REMINDER_NOTIFY_TYPES;
  readonly notificationPreferenceRows = DUNA_EVENT_NOTIFICATION_PREFERENCE_ROWS;
  resourceOverviewEventTypes = RESOURCE_OVERVIEW_EVENT_TYPES;
  groupCalendarRoleOptions = GROUP_CALENDAR_ROLE_OPTIONS;
  data: Record<string, unknown> = {};
  categoryColors: EventCategoryColorItem[] = [];
  resourceDefaultLocations: Location[] = [];
  resourceDefaultRooms: Room[] = [];
  loadingResourceDefaults = false;
  groupCalendarEditPaths: { path_id: number; name: string }[] = [];
  loadingGroupCalendarPaths = false;
  groupCalendarPathSearch = '';
  private cachedFilteredGroupCalendarPaths: { path_id: number; name: string }[] =
    [];
  private cachedFilteredGroupCalendarPathQuery = '';
  private cachedFilteredGroupCalendarPathsSource: {
    path_id: number;
    name: string;
  }[] | null = null;
  private roomsByLocationCache = new Map<string, Room[]>();
  private roomsByLocationCacheSource: Room[] | null = null;
  loading = false;
  saving = false;
  errorMessage: string | null = null;
  /** True only after settings were successfully loaded from the API (do not show form on 404/error). */
  settingsLoaded = false;

  priceGroups: OrgPriceGroupRow[] = [];
  priceGroupLabelDraft: Record<number, string> = {};
  newPriceGroupLabel = '';
  loadingPriceGroups = false;
  creatingPriceGroup = false;
  savingPriceGroupId: number | null = null;
  togglingPriceGroupId: number | null = null;
  deletingPriceGroupId: number | null = null;
  priceGroupsError: string | null = null;

  orgBookableResources: OrgBookableResourceRow[] = [];
  orgResourceLabelDraft: Record<number, string> = {};
  newOrgResourceLabel = '';
  loadingOrgResources = false;
  creatingOrgResource = false;
  savingOrgResourceId: number | null = null;
  togglingOrgResourceId: number | null = null;
  deletingOrgResourceId: number | null = null;
  orgResourcesError: string | null = null;

  private destroy$ = new Subject<void>();
  effectiveOrgId: number | null = null;
  /** True after we have attempted to resolve org id from auth (so we can show "no org" only when resolved). */
  orgIdResolved = false;

  constructor(
    private componentDataSourceService: ComponentDataSourceService,
    private componentDataDestService: ComponentDataDestService,
    private authService: AuthService,
    private http: HttpClient,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.resolveOrgId();
    if (this.effectiveOrgId != null) {
      this.loadSettings();
    } else {
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveOrgId(): void {
    if (this.organizationId != null) {
      this.effectiveOrgId =
        typeof this.organizationId === 'number'
          ? this.organizationId
          : parseInt(String(this.organizationId), 10);
      this.orgIdResolved = true;
      return;
    }
    this.authService
      .getUserInfo()
      .pipe(
        takeUntil(this.destroy$),
        filter((info): info is NonNullable<typeof info> => info != null)
      )
      .subscribe((info) => {
        const orgId =
          info?.user_info?.organizations_id ?? info?.organizations_id ?? null;
        this.effectiveOrgId =
          orgId != null
            ? typeof orgId === 'number'
              ? orgId
              : parseInt(String(orgId), 10)
            : null;
        this.orgIdResolved = true;
        if (this.effectiveOrgId != null && !this.data['name']) {
          this.loadSettings();
        }
        this.cdr.detectChanges();
      });
    // If not logged in, getUserInfo may never emit a non-null value; mark resolved after first emission (null or not)
    this.authService
      .getUserInfo()
      .pipe(takeUntil(this.destroy$), take(1))
      .subscribe((info) => {
        if (info == null && !this.orgIdResolved) {
          this.orgIdResolved = true;
          this.cdr.detectChanges();
        }
      });
  }

  private mergeDefaults(): void {
    const merged: Record<string, unknown> = {
      name: this.data['name'] ?? '',
      event_category_colors: this.data['event_category_colors'] ?? {},
    };
    this.checkboxDefs.forEach((def) => {
      merged[def.key] =
        this.data[def.key] !== undefined ? this.data[def.key] : def.default;
    });
    this.userAdminCheckboxDefs.forEach((def) => {
      merged[def.key] =
        this.data[def.key] !== undefined ? this.data[def.key] : def.default;
    });
    this.customerArchiveCheckboxDefs.forEach((def) => {
      merged[def.key] =
        this.data[def.key] !== undefined ? this.data[def.key] : def.default;
    });
    this.userGroupsCheckboxDefs.forEach((def) => {
      merged[def.key] =
        this.data[def.key] !== undefined ? this.data[def.key] : def.default;
    });
    this.materialsCheckboxDefs.forEach((def) => {
      merged[def.key] =
        this.data[def.key] !== undefined ? this.data[def.key] : def.default;
    });
    merged[MATERIALS_CATALOG_MODE_KEY] = 'own_org';
    merged[MATERIALS_PROVIDER_ORG_ID_KEY] = null;
    merged[MATERIALS_BORROWER_ORG_IDS_KEY] = [];
    const bufferRaw = this.data[MATERIALS_BUFFER_DAYS_KEY];
    const bufferNum =
      typeof bufferRaw === 'number'
        ? bufferRaw
        : parseInt(String(bufferRaw ?? 0), 10);
    merged[MATERIALS_BUFFER_DAYS_KEY] =
      Number.isFinite(bufferNum) && bufferNum >= 0 ? bufferNum : 0;
    const legacyUnregister = this.getCheckboxValue(
      'events_allow_unregister_24h'
    );
    const hasPerTypeUnregister = this.hasPerTypeUnregisterKeys();
    this.registerDeadlineTypes.forEach((t) => {
      const en = `${this.registerDeadlineEnabledKey(t.eventType)}`;
      const dk = `${this.registerDeadlineDaysKey(t.eventType)}`;
      merged[en] = this.data[en] !== undefined ? this.data[en] : false;
      const rawDays = this.data[dk];
      const n =
        typeof rawDays === 'number'
          ? rawDays
          : parseInt(String(rawDays ?? 0), 10);
      merged[dk] = Number.isFinite(n) && n >= 0 ? n : 0;
    });
    this.unregisterEventTypes.forEach((t) => {
      const en = this.unregisterEnabledKey(t.eventType);
      const dk = this.unregisterDaysKey(t.eventType);
      if (this.data[en] !== undefined) {
        merged[en] = this.data[en];
      } else if (legacyUnregister && !hasPerTypeUnregister) {
        merged[en] = true;
      } else {
        merged[en] = false;
      }
      const rawDays = this.data[dk];
      const n =
        typeof rawDays === 'number'
          ? rawDays
          : parseInt(String(rawDays ?? 1), 10);
      merged[dk] = Number.isFinite(n) && n >= 1 ? n : 1;
    });
    const legacyImplicitReminderOff = this.isLegacyImplicitReminderDisabled();
    this.eventReminderNotifyTypes.forEach((t) => {
      const en = this.eventReminderNotifyEnabledKey(t.eventType);
      const dk = this.eventReminderNotifyDaysKey(t.eventType);
      const extraEn = this.eventReminderExtraEnabledKey(t.eventType);
      const extraDk = this.eventReminderExtraDaysKey(t.eventType);
      const rawEnabled = this.data[en];
      merged[en] =
        legacyImplicitReminderOff ||
        rawEnabled === undefined ||
        rawEnabled === null
          ? DEFAULT_EVENT_REMINDER_ENABLED
          : this.isOrgSettingTrue(rawEnabled);
      const rawDays = this.data[dk];
      const n =
        typeof rawDays === 'number'
          ? rawDays
          : parseInt(String(rawDays ?? DEFAULT_EVENT_REMINDER_DAYS), 10);
      merged[dk] =
        Number.isFinite(n) && n >= 1 ? n : DEFAULT_EVENT_REMINDER_DAYS;
      merged[extraEn] =
        this.data[extraEn] !== undefined ? this.data[extraEn] : false;
      const rawExtraDays = this.data[extraDk];
      const extraN =
        typeof rawExtraDays === 'number'
          ? rawExtraDays
          : parseInt(String(rawExtraDays ?? 1), 10);
      merged[extraDk] = Number.isFinite(extraN) && extraN >= 1 ? extraN : 1;
    });
    merged[RESOURCE_OVERVIEW_LOCATION_KEY] = this.normalizeStringArray(
      this.data[RESOURCE_OVERVIEW_LOCATION_KEY]
    );
    merged[RESOURCE_OVERVIEW_ROOM_KEY] = this.normalizeStringArray(
      this.data[RESOURCE_OVERVIEW_ROOM_KEY]
    );
    const eventTypes = this.normalizeStringArray(
      this.data[RESOURCE_OVERVIEW_EVENT_TYPES_KEY]
    );
    merged[RESOURCE_OVERVIEW_EVENT_TYPES_KEY] =
      eventTypes.length > 0
        ? eventTypes.filter((t) =>
            this.resourceOverviewEventTypes.some((option) => option.value === t)
          )
        : this.resourceOverviewEventTypes.map((option) => option.value);
    merged[RESOURCE_OVERVIEW_SHOW_MANUAL_LOCATIONS_KEY] =
      this.resolveResourceOverviewShowManualLocations(
        this.data[RESOURCE_OVERVIEW_SHOW_MANUAL_LOCATIONS_KEY]
      );
    merged[RESOURCE_OVERVIEW_SHOW_LOCATION_ROWS_KEY] =
      this.resolveResourceOverviewShowLocationRows(
        this.data[RESOURCE_OVERVIEW_SHOW_LOCATION_ROWS_KEY]
      );
    merged[RESOURCE_OVERVIEW_ORG_RESOURCE_KEY] = this.normalizeNumberArray(
      this.data[RESOURCE_OVERVIEW_ORG_RESOURCE_KEY]
    );
    merged[EVENTS_ADDITIONAL_ROOMS_ENABLED_KEY] = this.isOrgSettingTrue(
      this.data[EVENTS_ADDITIONAL_ROOMS_ENABLED_KEY]
    );
    this.eventCateringFieldTypes.forEach((t) => {
      const key = this.cateringFieldEnabledKey(t.eventType);
      merged[key] =
        this.data[key] !== undefined ? this.isOrgSettingTrue(this.data[key]) : true;
    });
    this.eventModulesFieldTypes.forEach((t) => {
      const key = this.modulesFieldEnabledKey(t.eventType);
      merged[key] =
        this.data[key] !== undefined ? this.isOrgSettingTrue(this.data[key]) : true;
    });

    // Group calendar user-calendar settings
    merged[GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY] = this.isOrgSettingTrue(
      this.data[GROUP_CALENDAR_SHOW_ALL_LOCATIONS_KEY]
    );
    const rawViewRoles = this.normalizeStringArray(
      this.data[GROUP_CALENDAR_VIEW_ROLES_KEY]
    );
    const viewRoles =
      rawViewRoles.length > 0
        ? rawViewRoles
        : [...GROUP_CALENDAR_DEFAULT_VIEW_ROLES];
    // Always ensure orgbruger + orgadmin are present
    for (const r of GROUP_CALENDAR_ALWAYS_ON_ROLES) {
      if (!viewRoles.includes(r)) viewRoles.push(r);
    }
    merged[GROUP_CALENDAR_VIEW_ROLES_KEY] = viewRoles;
    merged[GROUP_CALENDAR_EDIT_PATH_IDS_KEY] = this.normalizeNumberArray(
      this.data[GROUP_CALENDAR_EDIT_PATH_IDS_KEY]
    );

    merged[NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY] =
      normalizeNotificationPreferencesMatrix(
        this.data[NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY]
      );

    this.data = merged;
    this.materialsCatalogMode = 'own_org';
    this.materialsProviderOrgId = null;
    this.materialsBorrowerOrgIdsText = '';
    this.materialsBufferDays =
      (merged[MATERIALS_BUFFER_DAYS_KEY] as number) ?? 0;
    const colors =
      (this.data['event_category_colors'] as Record<string, string>) || {};
    this.categoryColors = Object.entries(colors).map(([category, color]) => ({
      category,
      color: color || '#1976d2',
    }));
    if (this.categoryColors.length === 0) {
      this.categoryColors = [{ category: '', color: '#1976d2' }];
    }
  }

  loadSettings(): void {
    if (this.effectiveOrgId == null) return;
    this.loading = true;
    this.settingsLoaded = false;
    this.errorMessage = null;
    this.componentDataSourceService
      .executeDataSource('org_settings', {
        organizationId: this.effectiveOrgId,
      })
      ?.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: { data?: Record<string, unknown> }) => {
          this.data = res?.data ?? {};
          this.mergeDefaults();
          this.loadResourceOverviewOptions();
          if (this.getCheckboxValue('group_calendar_enabled')) {
            void this.loadGroupCalendarEditPaths();
          }
          this.settingsLoaded = true;
          this.loading = false;
          this.loadPriceGroups();
          this.loadOrgBookableResources();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.errorMessage = err?.message || 'Kunne ikke hente indstillinger';
          this.settingsLoaded = false;
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private loadResourceOverviewOptions(): void {
    this.loadingResourceDefaults = true;
    const locations$ = this.componentDataSourceService.executeDataSource(
      'locations',
      {
        filters: [
          {
            column: 'pageSize',
            value: DunaOrgSettingsComponent.RESOURCE_OVERVIEW_LIST_PAGE_SIZE,
            operator: 'eq',
          },
          { column: 'pageIndex', value: 0, operator: 'eq' },
        ],
      }
    );
    const rooms$ = this.componentDataSourceService.executeDataSource('rooms', {
      queryParams: {
        pageSize: DunaOrgSettingsComponent.RESOURCE_OVERVIEW_LIST_PAGE_SIZE,
        pageIndex: 0,
      },
    });

    if (!locations$ || !rooms$) {
      this.loadingResourceDefaults = false;
      return;
    }

    forkJoin({
      locations: locations$,
      rooms: rooms$,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ locations, rooms }) => {
          this.resourceDefaultLocations = (
            (locations?.items ?? []) as Location[]
          )
            .filter((location) => !!location?.guid)
            .sort((a, b) =>
              a.locations_name.localeCompare(b.locations_name, 'da-DK')
            );
          this.resourceDefaultRooms = ((rooms?.items ?? []) as Room[])
            .filter((room) => !!room?.guid)
            .sort((a, b) =>
              `${a.locations_name ?? ''}${a.room_name}`.localeCompare(
                `${b.locations_name ?? ''}${b.room_name}`,
                'da-DK'
              )
            );

          this.syncResourceDefaultsToKnownOptions();
          this.loadingResourceDefaults = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.resourceDefaultLocations = [];
          this.resourceDefaultRooms = [];
          this.loadingResourceDefaults = false;
          this.cdr.detectChanges();
        },
      });
  }

  getResourceDefaultLocations(): string[] {
    return this.normalizeStringArray(this.data[RESOURCE_OVERVIEW_LOCATION_KEY]);
  }

  getResourceDefaultRooms(): string[] {
    return this.normalizeStringArray(this.data[RESOURCE_OVERVIEW_ROOM_KEY]);
  }

  getResourceDefaultEventTypes(): string[] {
    const values = this.normalizeStringArray(
      this.data[RESOURCE_OVERVIEW_EVENT_TYPES_KEY]
    );
    return values.length > 0
      ? values
      : this.resourceOverviewEventTypes.map((option) => option.value);
  }

  isResourceDefaultLocationSelected(locationGuid: string): boolean {
    return this.getResourceDefaultLocations().includes(locationGuid);
  }

  isResourceDefaultRoomSelected(roomGuid: string): boolean {
    return this.getResourceDefaultRooms().includes(roomGuid);
  }

  isResourceDefaultEventTypeSelected(type: string): boolean {
    return this.getResourceDefaultEventTypes().includes(type);
  }

  getResourceOverviewShowManualLocations(): boolean {
    return this.resolveResourceOverviewShowManualLocations(
      this.data[RESOURCE_OVERVIEW_SHOW_MANUAL_LOCATIONS_KEY]
    );
  }

  setResourceOverviewShowManualLocations(value: boolean): void {
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_SHOW_MANUAL_LOCATIONS_KEY]: value,
    };
  }

  getResourceOverviewShowLocationRows(): boolean {
    return this.resolveResourceOverviewShowLocationRows(
      this.data[RESOURCE_OVERVIEW_SHOW_LOCATION_ROWS_KEY]
    );
  }

  setResourceOverviewShowLocationRows(value: boolean): void {
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_SHOW_LOCATION_ROWS_KEY]: value,
    };
  }

  /** Default is to show manual locations in resource overview; org can turn this off. */
  private resolveResourceOverviewShowManualLocations(value: unknown): boolean {
    if (value === false || value === 'false' || value === 0) {
      return false;
    }
    return true;
  }

  /** Default is to show location rows; org can turn this off so only rooms are shown when rooms exist. */
  private resolveResourceOverviewShowLocationRows(value: unknown): boolean {
    if (value === false || value === 'false' || value === 0) {
      return false;
    }
    return true;
  }

  onToggleResourceDefaultLocation(
    locationGuid: string,
    checked: boolean
  ): void {
    const selected = new Set(this.getResourceDefaultLocations());
    if (checked) {
      selected.add(locationGuid);
    } else {
      selected.delete(locationGuid);
      const roomsToRemove = this.resourceDefaultRooms
        .filter((room) => room.location_guid === locationGuid)
        .map((room) => room.guid);
      const selectedRooms = new Set(this.getResourceDefaultRooms());
      roomsToRemove.forEach((roomGuid) => selectedRooms.delete(roomGuid));
      this.data = {
        ...this.data,
        [RESOURCE_OVERVIEW_ROOM_KEY]: Array.from(selectedRooms),
      };
    }
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_LOCATION_KEY]: Array.from(selected),
    };
  }

  onToggleResourceDefaultRoom(roomGuid: string, checked: boolean): void {
    const selectedRooms = new Set(this.getResourceDefaultRooms());
    if (checked) {
      selectedRooms.add(roomGuid);
      const parentLocation = this.resourceDefaultRooms.find(
        (room) => room.guid === roomGuid
      )?.location_guid;
      if (parentLocation) {
        const selectedLocations = new Set(this.getResourceDefaultLocations());
        selectedLocations.add(parentLocation);
        this.data = {
          ...this.data,
          [RESOURCE_OVERVIEW_LOCATION_KEY]: Array.from(selectedLocations),
        };
      }
    } else {
      selectedRooms.delete(roomGuid);
    }
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_ROOM_KEY]: Array.from(selectedRooms),
    };
  }

  onToggleResourceDefaultEventType(type: string, checked: boolean): void {
    const selected = new Set(this.getResourceDefaultEventTypes());
    if (checked) {
      selected.add(type);
    } else {
      selected.delete(type);
    }
    const nextValues = Array.from(selected);
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_EVENT_TYPES_KEY]:
        nextValues.length > 0
          ? nextValues
          : this.resourceOverviewEventTypes.map((option) => option.value),
    };
  }

  getRoomsByLocation(locationGuid: string): Room[] {
    if (this.roomsByLocationCacheSource !== this.resourceDefaultRooms) {
      this.roomsByLocationCacheSource = this.resourceDefaultRooms;
      this.roomsByLocationCache.clear();
    }
    const cached = this.roomsByLocationCache.get(locationGuid);
    if (cached) {
      return cached;
    }
    const rooms = this.resourceDefaultRooms.filter(
      (room) => room.location_guid === locationGuid
    );
    this.roomsByLocationCache.set(locationGuid, rooms);
    return rooms;
  }

  private syncResourceDefaultsToKnownOptions(): void {
    const knownLocations = new Set(
      this.resourceDefaultLocations.map((location) => location.guid)
    );
    const knownRooms = new Set(
      this.resourceDefaultRooms.map((room) => room.guid)
    );

    const currentLocations = this.getResourceDefaultLocations().filter((guid) =>
      knownLocations.has(guid)
    );
    const currentRooms = this.getResourceDefaultRooms().filter((guid) =>
      knownRooms.has(guid)
    );

    // Default behavior: if nothing is configured yet, preselect all locations and rooms.
    const validLocations =
      currentLocations.length > 0
        ? currentLocations
        : this.resourceDefaultLocations.map((location) => location.guid);
    const validRooms =
      currentRooms.length > 0
        ? currentRooms
        : this.resourceDefaultRooms.map((room) => room.guid);

    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_LOCATION_KEY]: validLocations,
      [RESOURCE_OVERVIEW_ROOM_KEY]: validRooms,
    };
  }

  private normalizeStringArray(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }
    return [];
  }

  private normalizeNumberArray(value: unknown): number[] {
    if (Array.isArray(value)) {
      return value
        .map((item) => {
          const n =
            typeof item === 'number' ? item : parseInt(String(item), 10);
          return Number.isFinite(n) ? n : null;
        })
        .filter((n): n is number => n !== null);
    }
    return [];
  }

  // --- Group calendar user-calendar settings ---

  private async loadGroupCalendarEditPaths(): Promise<void> {
    this.loadingGroupCalendarPaths = true;
    this.cdr.detectChanges();
    try {
      const result = await firstValueFrom(
        this.componentDataSourceService.executeDataSource('educational_paths', {
          filters: [
            { column: 'pageSize', value: 1000, operator: 'eq' },
            { column: 'pageIndex', value: 0, operator: 'eq' },
          ],
        })!
      );
      const items = result?.items ?? [];
      this.groupCalendarEditPaths = (Array.isArray(items) ? items : [])
        .map((p: { path_id: number; name?: string; full_name?: string }) => ({
          path_id: p.path_id,
          name: p.name || p.full_name || String(p.path_id),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'da-DK'));
    } catch {
      this.groupCalendarEditPaths = [];
    } finally {
      this.loadingGroupCalendarPaths = false;
      this.cdr.detectChanges();
    }
  }

  getGroupCalendarViewRoles(): string[] {
    const raw = this.normalizeStringArray(
      this.data[GROUP_CALENDAR_VIEW_ROLES_KEY]
    );
    if (raw.length === 0) {
      return [
        ...GROUP_CALENDAR_DEFAULT_VIEW_ROLES,
        ...GROUP_CALENDAR_ALWAYS_ON_ROLES,
      ];
    }
    return raw;
  }

  isGroupCalendarViewRoleSelected(role: string): boolean {
    return this.getGroupCalendarViewRoles().includes(role);
  }

  onToggleGroupCalendarViewRole(role: string, checked: boolean): void {
    const selected = new Set(this.getGroupCalendarViewRoles());
    if (checked) {
      selected.add(role);
    } else {
      selected.delete(role);
    }
    // Always keep always-on roles
    for (const r of GROUP_CALENDAR_ALWAYS_ON_ROLES) {
      selected.add(r);
    }
    this.data = {
      ...this.data,
      [GROUP_CALENDAR_VIEW_ROLES_KEY]: Array.from(selected),
    };
  }

  get filteredGroupCalendarEditPaths(): { path_id: number; name: string }[] {
    const q = this.groupCalendarPathSearch.trim().toLowerCase();
    if (!q) {
      return this.groupCalendarEditPaths;
    }
    if (
      this.cachedFilteredGroupCalendarPathsSource ===
        this.groupCalendarEditPaths &&
      this.cachedFilteredGroupCalendarPathQuery === q
    ) {
      return this.cachedFilteredGroupCalendarPaths;
    }
    this.cachedFilteredGroupCalendarPathsSource = this.groupCalendarEditPaths;
    this.cachedFilteredGroupCalendarPathQuery = q;
    this.cachedFilteredGroupCalendarPaths = this.groupCalendarEditPaths.filter(
      (p) => p.name.toLowerCase().includes(q)
    );
    return this.cachedFilteredGroupCalendarPaths;
  }

  getGroupCalendarEditPathIds(): number[] {
    return this.normalizeNumberArray(
      this.data[GROUP_CALENDAR_EDIT_PATH_IDS_KEY]
    );
  }

  isGroupCalendarEditPathSelected(pathId: number): boolean {
    return this.getGroupCalendarEditPathIds().includes(pathId);
  }

  onToggleGroupCalendarEditPath(pathId: number, checked: boolean): void {
    const selected = new Set(this.getGroupCalendarEditPathIds());
    if (checked) {
      selected.add(pathId);
    } else {
      selected.delete(pathId);
    }
    this.data = {
      ...this.data,
      [GROUP_CALENDAR_EDIT_PATH_IDS_KEY]: Array.from(selected),
    };
  }

  onGroupCalendarEnabledChange(checked: boolean): void {
    this.setCheckboxValue('group_calendar_enabled', checked);
    if (checked && this.groupCalendarEditPaths.length === 0) {
      void this.loadGroupCalendarEditPaths();
    }
  }

  getCheckboxValue(key: string): boolean {
    const v = this.data[key];
    return v === true || v === 'true' || v === 1;
  }

  setCheckboxValue(key: string, value: boolean): void {
    this.data = { ...this.data, [key]: value };
  }

  cateringFieldEnabledKey(eventType: string): string {
    return cateringFieldEnabledOrgKey(eventType);
  }

  getCateringFieldEnabled(eventType: string): boolean {
    const key = this.cateringFieldEnabledKey(eventType);
    const raw = this.data[key];
    if (raw === undefined || raw === null) {
      return true;
    }
    return this.isOrgSettingTrue(raw);
  }

  setCateringFieldEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.cateringFieldEnabledKey(eventType), value);
  }

  modulesFieldEnabledKey(eventType: string): string {
    return modulesFieldEnabledOrgKey(eventType);
  }

  getModulesFieldEnabled(eventType: string): boolean {
    const key = this.modulesFieldEnabledKey(eventType);
    const raw = this.data[key];
    if (raw === undefined || raw === null) {
      return true;
    }
    return this.isOrgSettingTrue(raw);
  }

  setModulesFieldEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.modulesFieldEnabledKey(eventType), value);
  }

  registerDeadlineEnabledKey(eventType: string): string {
    return `events_register_deadline_${eventType}_enabled`;
  }

  registerDeadlineDaysKey(eventType: string): string {
    return `events_register_deadline_${eventType}_days`;
  }

  getRegisterDeadlineEnabled(eventType: string): boolean {
    return this.getCheckboxValue(this.registerDeadlineEnabledKey(eventType));
  }

  setRegisterDeadlineEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.registerDeadlineEnabledKey(eventType), value);
  }

  getRegisterDeadlineDays(eventType: string): number {
    const v = this.data[this.registerDeadlineDaysKey(eventType)];
    const n = typeof v === 'number' ? v : parseInt(String(v ?? 0), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  onRegisterDeadlineDaysChange(
    eventType: string,
    value: string | number
  ): void {
    const n = typeof value === 'number' ? value : parseInt(String(value), 10);
    const days = Number.isFinite(n) && n >= 0 ? n : 0;
    this.data = {
      ...this.data,
      [this.registerDeadlineDaysKey(eventType)]: days,
    };
  }

  private hasPerTypeUnregisterKeys(): boolean {
    return this.unregisterEventTypes.some(
      (t) => this.data[this.unregisterEnabledKey(t.eventType)] !== undefined
    );
  }

  unregisterEnabledKey(eventType: string): string {
    return `events_allow_unregister_${eventType}_enabled`;
  }

  unregisterDaysKey(eventType: string): string {
    return `events_allow_unregister_${eventType}_days`;
  }

  getUnregisterEnabled(eventType: string): boolean {
    return this.getCheckboxValue(this.unregisterEnabledKey(eventType));
  }

  setUnregisterEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.unregisterEnabledKey(eventType), value);
  }

  getUnregisterDays(eventType: string): number {
    const v = this.data[this.unregisterDaysKey(eventType)];
    const n = typeof v === 'number' ? v : parseInt(String(v ?? 1), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  onUnregisterDaysChange(eventType: string, value: string | number): void {
    const n = typeof value === 'number' ? value : parseInt(String(value), 10);
    const days = Number.isFinite(n) && n >= 1 ? n : 1;
    this.data = { ...this.data, [this.unregisterDaysKey(eventType)]: days };
  }

  eventReminderNotifyEnabledKey(eventType: string): string {
    return `events_notify_reminder_${eventType}_enabled`;
  }

  eventReminderNotifyDaysKey(eventType: string): string {
    return `events_notify_reminder_${eventType}_days`;
  }

  private isOrgSettingTrue(value: unknown): boolean {
    return value === true || value === 'true' || value === 1;
  }

  private isOrgSettingFalse(value: unknown): boolean {
    return value === false || value === 'false' || value === 0;
  }

  private hasPerTypeReminderEnabledKeys(): boolean {
    return this.eventReminderNotifyTypes.some((t) => {
      const v = this.data[this.eventReminderNotifyEnabledKey(t.eventType)];
      return v !== undefined && v !== null;
    });
  }

  private hasAnyReminderExtraKeys(): boolean {
    return this.eventReminderNotifyTypes.some(
      (t) =>
        this.data[this.eventReminderExtraEnabledKey(t.eventType)] !== undefined
    );
  }

  /**
   * Tidligere UI gemte alle påmindelser som slået fra som standard.
   * Behandles som ikke-konfigureret når ingen ekstra-påmindelse findes.
   */
  private isLegacyImplicitReminderDisabled(): boolean {
    if (
      !this.hasPerTypeReminderEnabledKeys() ||
      this.hasAnyReminderExtraKeys()
    ) {
      return false;
    }
    return this.eventReminderNotifyTypes.every((t) =>
      this.isOrgSettingFalse(
        this.data[this.eventReminderNotifyEnabledKey(t.eventType)]
      )
    );
  }

  getEventReminderNotifyEnabled(eventType: string): boolean {
    const key = this.eventReminderNotifyEnabledKey(eventType);
    const raw = this.data[key];
    if (
      this.isLegacyImplicitReminderDisabled() ||
      raw === undefined ||
      raw === null
    ) {
      return DEFAULT_EVENT_REMINDER_ENABLED;
    }
    return this.isOrgSettingTrue(raw);
  }

  setEventReminderNotifyEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.eventReminderNotifyEnabledKey(eventType), value);
  }

  getEventReminderNotifyDays(eventType: string): number {
    const v = this.data[this.eventReminderNotifyDaysKey(eventType)];
    const n =
      typeof v === 'number'
        ? v
        : parseInt(String(v ?? DEFAULT_EVENT_REMINDER_DAYS), 10);
    return Number.isFinite(n) && n >= 1 ? n : DEFAULT_EVENT_REMINDER_DAYS;
  }

  onEventReminderNotifyDaysChange(
    eventType: string,
    value: string | number
  ): void {
    const n = typeof value === 'number' ? value : parseInt(String(value), 10);
    const days = Number.isFinite(n) && n >= 1 ? n : DEFAULT_EVENT_REMINDER_DAYS;
    this.data = {
      ...this.data,
      [this.eventReminderNotifyDaysKey(eventType)]: days,
    };
  }

  eventReminderExtraEnabledKey(eventType: string): string {
    return `events_notify_reminder_${eventType}_extra_enabled`;
  }

  eventReminderExtraDaysKey(eventType: string): string {
    return `events_notify_reminder_${eventType}_extra_days`;
  }

  getEventReminderExtraEnabled(eventType: string): boolean {
    return this.getCheckboxValue(this.eventReminderExtraEnabledKey(eventType));
  }

  setEventReminderExtraEnabled(eventType: string, value: boolean): void {
    this.setCheckboxValue(this.eventReminderExtraEnabledKey(eventType), value);
  }

  getEventReminderExtraDays(eventType: string): number {
    const v = this.data[this.eventReminderExtraDaysKey(eventType)];
    const n = typeof v === 'number' ? v : parseInt(String(v ?? 1), 10);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  }

  onEventReminderExtraDaysChange(
    eventType: string,
    value: string | number
  ): void {
    const n = typeof value === 'number' ? value : parseInt(String(value), 10);
    const days = Number.isFinite(n) && n >= 1 ? n : 1;
    this.data = {
      ...this.data,
      [this.eventReminderExtraDaysKey(eventType)]: days,
    };
  }

  private getOrgNotificationMatrix(): NotificationPreferencesMatrix {
    return normalizeNotificationPreferencesMatrix(
      this.data[NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY]
    );
  }

  getNotificationPrefEmail(key: string): boolean {
    return this.getOrgNotificationMatrix()[key]?.email ?? true;
  }

  getNotificationPrefPush(key: string): boolean {
    return this.getOrgNotificationMatrix()[key]?.push ?? true;
  }

  setNotificationPrefEmail(key: string, value: boolean): void {
    const matrix = { ...this.getOrgNotificationMatrix() };
    const row = matrix[key] ?? { email: true, push: true };
    matrix[key] = { ...row, email: value };
    this.data = {
      ...this.data,
      [NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY]: matrix,
    };
  }

  setNotificationPrefPush(key: string, value: boolean): void {
    const matrix = { ...this.getOrgNotificationMatrix() };
    const row = matrix[key] ?? { email: true, push: true };
    matrix[key] = { ...row, push: value };
    this.data = {
      ...this.data,
      [NOTIFICATION_PREFERENCES_ORG_DEFAULTS_KEY]: matrix,
    };
  }

  save(): void {
    if (this.effectiveOrgId == null) {
      this.errorMessage = 'Ingen organisation valgt';
      return;
    }
    const colorsObj: Record<string, string> = {};
    this.categoryColors.forEach((item) => {
      const cat = (item.category || '').trim();
      if (cat) colorsObj[cat] = item.color || '#1976d2';
    });
    // Ensure always-on roles are present before saving
    const viewRoles = new Set(this.getGroupCalendarViewRoles());
    for (const r of GROUP_CALENDAR_ALWAYS_ON_ROLES) {
      viewRoles.add(r);
    }
    const payload = {
      ...this.data,
      event_category_colors: colorsObj,
      [GROUP_CALENDAR_VIEW_ROLES_KEY]: Array.from(viewRoles),
      [MATERIALS_CATALOG_MODE_KEY]: 'own_org',
      [MATERIALS_PROVIDER_ORG_ID_KEY]: null,
      [MATERIALS_BORROWER_ORG_IDS_KEY]: [],
      [MATERIALS_BUFFER_DAYS_KEY]: Math.max(
        0,
        Math.min(30, this.materialsBufferDays || 0)
      ),
    };
    this.saving = true;
    this.errorMessage = null;
    const dest = this.componentDataDestService.getDataDest(
      'update_org_settings'
    );
    if (!dest) {
      this.errorMessage = 'Opdatering af indstillinger er ikke tilgængeligt';
      this.saving = false;
      this.cdr.detectChanges();
      return;
    }
    const obs = dest({
      organizationId: this.effectiveOrgId,
      data: payload,
    }) as Observable<unknown>;
    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.saving = false;
        this.cdr.detectChanges();
      },
      error: (err: unknown) => {
        this.errorMessage =
          (err as { message?: string })?.message ||
          'Kunne ikke gemme indstillinger';
        this.saving = false;
        this.cdr.detectChanges();
      },
    });
  }

  addCategoryColor(): void {
    this.categoryColors.push({ category: '', color: '#1976d2' });
    this.cdr.detectChanges();
  }

  removeCategoryColor(index: number): void {
    this.categoryColors.splice(index, 1);
    if (this.categoryColors.length === 0) {
      this.categoryColors = [{ category: '', color: '#1976d2' }];
    }
    this.cdr.detectChanges();
  }

  trackByIndex(index: number): number {
    return index;
  }

  loadPriceGroups(): void {
    this.loadingPriceGroups = true;
    this.priceGroupsError = null;
    this.http
      .get<{ items?: OrgPriceGroupRow[] }>(
        `${environment.apiUrl}/events/price-groups`
      )
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.loadingPriceGroups = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          const items = (res?.items ?? []) as OrgPriceGroupRow[];
          this.priceGroups = [...items].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
          );
          this.syncPriceGroupDrafts();
        },
        error: () => {
          this.priceGroups = [];
          this.priceGroupLabelDraft = {};
          this.priceGroupsError = 'Kunne ikke hente prisgrupper.';
        },
      });
  }

  private syncPriceGroupDrafts(): void {
    const next: Record<number, string> = {};
    this.priceGroups.forEach((g) => {
      next[g.id] = g.label;
    });
    this.priceGroupLabelDraft = next;
  }

  priceGroupLabelDirty(group: OrgPriceGroupRow): boolean {
    return (
      (this.priceGroupLabelDraft[group.id] ?? '').trim() !==
      (group.label || '').trim()
    );
  }

  createPriceGroup(): void {
    const label = this.newPriceGroupLabel.trim();
    if (!label) {
      this.priceGroupsError = 'Angiv et navn for prisgruppen.';
      return;
    }
    this.creatingPriceGroup = true;
    this.priceGroupsError = null;
    this.http
      .post(`${environment.apiUrl}/events/price-groups`, { label })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.creatingPriceGroup = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.newPriceGroupLabel = '';
          this.loadPriceGroups();
        },
        error: (err) => {
          this.priceGroupsError =
            err?.error?.message || 'Kunne ikke oprette prisgruppe.';
        },
      });
  }

  savePriceGroupLabel(group: OrgPriceGroupRow): void {
    const label = (this.priceGroupLabelDraft[group.id] ?? '').trim();
    if (!label) {
      this.priceGroupsError = 'Navn må ikke være tomt.';
      return;
    }
    if (!this.priceGroupLabelDirty(group)) return;
    this.savingPriceGroupId = group.id;
    this.priceGroupsError = null;
    this.http
      .patch(`${environment.apiUrl}/events/price-groups/${group.id}`, { label })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.savingPriceGroupId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.loadPriceGroups(),
        error: (err) => {
          this.priceGroupsError =
            err?.error?.message || 'Kunne ikke gemme prisgruppe.';
        },
      });
  }

  onPriceGroupActiveChange(
    group: OrgPriceGroupRow,
    ev: MatSlideToggleChange
  ): void {
    const next = ev.checked;
    this.togglingPriceGroupId = group.id;
    this.priceGroupsError = null;
    this.http
      .patch(`${environment.apiUrl}/events/price-groups/${group.id}`, {
        is_active: next,
      })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.togglingPriceGroupId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.loadPriceGroups(),
        error: (err) => {
          ev.source.checked = !next;
          this.priceGroupsError =
            err?.error?.message || 'Kunne ikke ændre aktiv status.';
        },
      });
  }

  deletePriceGroup(group: OrgPriceGroupRow): void {
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      width: 'min(520px, 92vw)',
      data: {
        title: 'Slet prisgruppe?',
        message: `Vil du slette «${group.label}»?`,
        details: [
          'Eksisterende events og kunder påvirkes ikke.',
          'Deaktiver i stedet, hvis gruppen ikke skal vises i nye events.',
        ],
        confirmText: 'Ja, slet',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });
    ref
      .afterClosed()
      .pipe(rxTakeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.deletingPriceGroupId = group.id;
        this.priceGroupsError = null;
        this.http
          .delete(`${environment.apiUrl}/events/price-groups/${group.id}`)
          .pipe(
            rxTakeUntil(this.destroy$),
            finalize(() => {
              this.deletingPriceGroupId = null;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: () => this.loadPriceGroups(),
            error: (err) => {
              this.priceGroupsError =
                err?.error?.message || 'Kunne ikke slette prisgruppe.';
            },
          });
      });
  }

  getEventsAdditionalRoomsEnabled(): boolean {
    return this.isOrgSettingTrue(
      this.data[EVENTS_ADDITIONAL_ROOMS_ENABLED_KEY]
    );
  }

  setEventsAdditionalRoomsEnabled(value: boolean): void {
    this.data = { ...this.data, [EVENTS_ADDITIONAL_ROOMS_ENABLED_KEY]: value };
  }

  getResourceDefaultOrgResourceIds(): number[] {
    return this.normalizeNumberArray(
      this.data[RESOURCE_OVERVIEW_ORG_RESOURCE_KEY]
    );
  }

  isResourceDefaultOrgResourceSelected(resourceId: number): boolean {
    return this.getResourceDefaultOrgResourceIds().includes(resourceId);
  }

  onToggleResourceDefaultOrgResource(
    resourceId: number,
    checked: boolean
  ): void {
    const selected = new Set(this.getResourceDefaultOrgResourceIds());
    if (checked) {
      selected.add(resourceId);
    } else {
      selected.delete(resourceId);
    }
    this.data = {
      ...this.data,
      [RESOURCE_OVERVIEW_ORG_RESOURCE_KEY]: Array.from(selected),
    };
  }

  loadOrgBookableResources(): void {
    this.loadingOrgResources = true;
    this.orgResourcesError = null;
    this.http
      .get<{ items?: OrgBookableResourceRow[] }>(
        `${environment.apiUrl}/events/org-resources`
      )
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.loadingOrgResources = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res) => {
          const items = (res?.items ?? []) as OrgBookableResourceRow[];
          this.orgBookableResources = [...items].sort(
            (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
          );
          this.syncOrgResourceDrafts();
        },
        error: () => {
          this.orgBookableResources = [];
          this.orgResourceLabelDraft = {};
          this.orgResourcesError = 'Kunne ikke hente bookbare ressourcer.';
        },
      });
  }

  private syncOrgResourceDrafts(): void {
    const next: Record<number, string> = {};
    this.orgBookableResources.forEach((r) => {
      next[r.id] = r.label;
    });
    this.orgResourceLabelDraft = next;
  }

  orgResourceLabelDirty(resource: OrgBookableResourceRow): boolean {
    return (
      (this.orgResourceLabelDraft[resource.id] ?? '').trim() !==
      (resource.label || '').trim()
    );
  }

  createOrgBookableResource(): void {
    const label = this.newOrgResourceLabel.trim();
    if (!label) {
      this.orgResourcesError = 'Angiv et navn for ressourcen.';
      return;
    }
    this.creatingOrgResource = true;
    this.orgResourcesError = null;
    this.http
      .post(`${environment.apiUrl}/events/org-resources`, { label })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.creatingOrgResource = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          this.newOrgResourceLabel = '';
          this.loadOrgBookableResources();
        },
        error: (err) => {
          this.orgResourcesError =
            err?.error?.message || 'Kunne ikke oprette ressource.';
        },
      });
  }

  saveOrgResourceLabel(resource: OrgBookableResourceRow): void {
    const label = (this.orgResourceLabelDraft[resource.id] ?? '').trim();
    if (!label) {
      this.orgResourcesError = 'Navn må ikke være tomt.';
      return;
    }
    if (!this.orgResourceLabelDirty(resource)) return;
    this.savingOrgResourceId = resource.id;
    this.orgResourcesError = null;
    this.http
      .patch(`${environment.apiUrl}/events/org-resources/${resource.id}`, {
        label,
      })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.savingOrgResourceId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.loadOrgBookableResources(),
        error: (err) => {
          this.orgResourcesError =
            err?.error?.message || 'Kunne ikke gemme ressource.';
        },
      });
  }

  onOrgResourceActiveChange(
    resource: OrgBookableResourceRow,
    ev: MatSlideToggleChange
  ): void {
    const next = ev.checked;
    this.togglingOrgResourceId = resource.id;
    this.orgResourcesError = null;
    this.http
      .patch(`${environment.apiUrl}/events/org-resources/${resource.id}`, {
        is_active: next,
      })
      .pipe(
        rxTakeUntil(this.destroy$),
        finalize(() => {
          this.togglingOrgResourceId = null;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.loadOrgBookableResources(),
        error: (err) => {
          ev.source.checked = !next;
          this.orgResourcesError =
            err?.error?.message || 'Kunne ikke ændre aktiv status.';
        },
      });
  }

  deleteOrgBookableResource(resource: OrgBookableResourceRow): void {
    const ref = this.dialog.open(ConfirmResetDialogComponent, {
      width: 'min(520px, 92vw)',
      data: {
        title: 'Slet ressource?',
        message: `Vil du slette «${resource.label}»?`,
        details: [
          'Ressourcen kan ikke slettes hvis den er knyttet til events.',
          'Deaktiver i stedet, hvis den ikke skal kunne vælges i nye events.',
        ],
        confirmText: 'Ja, slet',
        cancelText: 'Annuller',
        confirmColor: 'warn',
      },
    });
    ref
      .afterClosed()
      .pipe(rxTakeUntil(this.destroy$))
      .subscribe((confirmed: boolean) => {
        if (!confirmed) return;
        this.deletingOrgResourceId = resource.id;
        this.orgResourcesError = null;
        this.http
          .delete(`${environment.apiUrl}/events/org-resources/${resource.id}`)
          .pipe(
            rxTakeUntil(this.destroy$),
            finalize(() => {
              this.deletingOrgResourceId = null;
              this.cdr.detectChanges();
            })
          )
          .subscribe({
            next: () => this.loadOrgBookableResources(),
            error: (err) => {
              this.orgResourcesError =
                err?.error?.message || 'Kunne ikke slette ressource.';
            },
          });
      });
  }
}
